import React, { useState, useEffect } from "react";

import useDataSourceManager from "hooks/useDataSourceManager";
import useZustandStore from "zustandStore";
import { isLicenseManagementEnabled } from "utils/licenseFeature";

import { v4 as uuidv4 } from "uuid";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { styled, useTheme } from "@mui/material/styles";
import { Box, Typography, Divider, CircularProgress, Stack, IconButton } from "@mui/material";
import CaretRightFilled from "@ant-design/icons/CaretRightFilled";
import WarningFilled from "@ant-design/icons/WarningFilled";
import CloseOutlined from "@ant-design/icons/CloseOutlined";

import CollapseCard from "components/Collapse";
import { OutlineButton } from "components/buttons/OutlineButton";
import { MenuSelect } from "components/Select/menuSelect";
import ModalContainer from "components/ModalContainer";
import { SolidWarnButton } from "components/buttons/SolidWarnButton";
import { SolidButton } from "components/buttons/SolidButton";
import GlobalAlert from "components/Notification";

const isPremises = process.env.REACT_APP_RUNNING_CONTEXT === "premises";

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  fontWeight: theme.typography.customFontWeight[1],
}));

dayjs.extend(utc);
dayjs.extend(timezone);

const ManagementPanel = ({
  auth,
  capabilityData,
  infoData,
  isInventoryDevice,
  fetchTableData,
  handleDrawerClose,
  settingsData
}) => {
  const theme = useTheme();
  const portalConfig = useZustandStore((state) => state.portalConfig);
  const licenseManagementEnabled = isLicenseManagementEnabled(portalConfig);
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [currentModal, setCurrentModal] = useState("");
  const [displayModal, setDisplayModal] = useState(false);
  const [showConfirmServiceChangeModal, setShowConfirmServiceChangeModal] = useState(false);

  const [preConfirmSocketId, setPreConfirmSocketId] = useState(null);
  const [preConfirmResult, setPreConfirmResult] = useState(null);
  const [isPreConfirmLoading, setIsPreConfirmLoading] = useState(false);
  const [activePreConfirmData, setActivePreConfirmData] = useState(null);

  const [tunnelAccessTerminalUuid, _setTunnelAccessTerminalUuid] = useState(uuidv4());
  const [tunnelAccessWebUuid, _setTunnelAccessWebUuid] = useState(uuidv4());
  const [isTunnelAccesstTerminalLoading, setIsTunnelAccesstTerminalLoading] = useState(false);
  const [isTunnelAccesstWebLoading, setIsTunnelAccesstWebLoading] = useState(false);

  const [sitesUuid, _setSitesUuid] = useState(uuidv4());
  const [orgMoveSitesUuid, _orgMoveSetSitesUuid] = useState(uuidv4());
  const [siteMoveSitesUuid, _siteMoveSetSitesUuid] = useState(uuidv4());
  const [orgRemoveFromSiteUuid, _orgRemoveFromSiteUuid] = useState(uuidv4());
  const [orgDeleteDeviceUuid, _orgDeleteDeviceUuid] = useState(uuidv4());

  const timezone = settingsData?.timeConfig?.timezone || "UTC";
  const [sitesData, setSitesData] = useState([]);
  const [selectedMoveSite, setSelectedMoveSite] = useState(null);
  const [updateExecuteCommandUuid, _setUpdateExecuteCommandUuid] = useState(uuidv4());
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const [isStartLoading, setIsStartLoading] = useState(false);
  const [isStopLoading, setIsStopLoading] = useState(false);
  const standalone = Object.keys(auth.orgs).length === 0;
  const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
  const isSurveillanceDevice = ["nvr", "ipcam"].includes(infoData?.deviceType);

  const getIsInsufficient = () => {
    if (activePreConfirmData) {
      const sitesToRender = activePreConfirmData.filter(item => item.service && item.service.length > 0);
      return sitesToRender.some(item => item.sufficientCredits === false);
    }
    return false;
  };

  function handleButtonOnClick(actionKey) {
    let restfulParams = standalone
      ? {
        orgId: auth.realm.id,
        siteId: auth.sites?.id,
      }
      : {
        entId: auth.realm.id,
        orgId: auth.orgs.id,
        siteId: auth.sites?.id,
      };

    if (
      actionKey === "tunnelAccessTerminal" ||
      actionKey === "tunnelAccessTerminalWeb"
    ) {
      restfulParams = {
        ...restfulParams,
        deviceId: infoData.macAddress,
      };
    }

    let moveToSiteUrl = `${baseUrl}/devices/assignSite.update`;
    if (isInventoryDevice) {
      if (actionKey === "moveToSite" && auth.scopeLevel === "site") {
        moveToSiteUrl = `${baseUrl}/sites/:siteId/devices/addFromInventory.update`;
        restfulParams = standalone
          ? {
            orgId: auth.realm.id,
            siteId: selectedMoveSite.siteId,
          }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            siteId: selectedMoveSite.siteId,
          };
      }
    }

    const requestConfig = {
      tunnelAccessTerminal: {
        org: {
          url: `${baseUrl}/devices/:deviceId/tunnelAccess.create`,
          uuid: tunnelAccessTerminalUuid,
          getRequestParams: () => ({
            data: {
              type: "ReverseSsh",
              serviceType: "CLI",
              deviceId: infoData.macAddress,
            },
          }),
        },
        site: {
          url: `${baseUrl}/sites/:siteId/devices/:deviceId/tunnelAccess.create`,
          uuid: tunnelAccessTerminalUuid,
          getRequestParams: () => ({
            data: {
              type: "ReverseSsh",
              serviceType: "CLI",
              deviceId: infoData.macAddress,
            },
          }),
        },
      },
      tunnelAccessTerminalWeb: {
        org: {
          url: `${baseUrl}/devices/:deviceId/tunnelAccess.create`,
          uuid: tunnelAccessWebUuid,
          getRequestParams: () => ({
            data: {
              type: "ReverseSsh",
              serviceType: "WEB",
              deviceId: infoData.macAddress,
            },
          }),
        },
        site: {
          url: `${baseUrl}/sites/:siteId/devices/:deviceId/tunnelAccess.create`,
          uuid: tunnelAccessWebUuid,
          getRequestParams: () => ({
            data: {
              type: "ReverseSsh",
              serviceType: "WEB",
              deviceId: infoData.macAddress,
            },
          }),
        },
      },
      moveToSite: {
        org: {
          url: moveToSiteUrl,
          uuid: orgMoveSitesUuid,
          getRequestParams: () => ({
            siteId: selectedMoveSite.siteId,
            deviceIds: [infoData.macAddress],
          }),
        },
        site: {
          url: moveToSiteUrl,
          uuid: siteMoveSitesUuid,
          getRequestParams: () =>
            isInventoryDevice
              ? [{ deviceId: infoData.macAddress }]
              : [infoData.macAddress],
        },
      },
      removeFromSite: {
        org: {
          url: `${baseUrl}/devices/moveToInventory.update`,
          uuid: orgRemoveFromSiteUuid,
          getRequestParams: () => ({ data: [{ deviceId: infoData.macAddress }] }),
        },
        site: {
          url: `${baseUrl}/devices/moveToInventory.update`,
          uuid: orgRemoveFromSiteUuid,
          getRequestParams: () => ({
            data: [{ deviceId: infoData.macAddress }],
          }),
        },
      },
      deleteDevice: {
        org: {
          url: `${baseUrl}/devices/remove.update`,
          uuid: orgDeleteDeviceUuid,
          getRequestParams: () => ({
            data: [{ deviceId: infoData.deviceType === "proxy" ? infoData.id : infoData.macAddress }],
          }),
        },
        site: {
          url: `${baseUrl}/devices/remove.update`,
          uuid: orgDeleteDeviceUuid,
          getRequestParams: () => ({
            data: [{ deviceId: infoData.deviceType === "proxy" ? infoData.id : infoData.macAddress }],
          }),
        },
      },
    };

    if (actionKey === "tunnelAccessTerminal")
      setIsTunnelAccesstTerminalLoading(true);
    if (actionKey === "tunnelAccessTerminalWeb")
      setIsTunnelAccesstWebLoading(true);

    const actionConfig = requestConfig[actionKey]?.[auth.scopeLevel];
    if (actionConfig) {
      const requestParams = actionConfig.getRequestParams();
      sendRequest(
        actionConfig.url,
        actionConfig.uuid,
        restfulParams,
        requestParams
      );
    }

    fetchTableData();
  }

  const handleExecuteCommand = (commandName) => {
    if (commandName === "lightUpLed") setIsStartLoading(true);
    if (commandName === "stoplightUpLed") setIsStopLoading(true);
    const standalone = !auth.orgs?.id;
    const entId = auth.realm?.id;
    const orgId = auth.orgs?.id || auth.realm?.id;
    const siteId = infoData.siteId || auth.sites?.id;

    const resourcePath =
      !standalone
        ? "/ents/:entId/orgs/:orgId/sites/:siteId/devices/:deviceId/executeCommand.update"
        : "/orgs/:orgId/devices/:deviceId/executeCommand.update";

    const restfulParams =
      !standalone
        ? { entId, orgId, siteId, deviceId: infoData.macAddress }
        : { orgId, siteId, deviceId: infoData.macAddress };

    const requestParams = {
      data: {
        command: commandName,
        params: {},
      },
    };

    sendRequest(
      resourcePath,
      updateExecuteCommandUuid,
      restfulParams,
      requestParams
    );
  };

  useEffect(() => {
    const executeCommandResponse = getData(updateExecuteCommandUuid);
    if (!executeCommandResponse) return;

    const result = executeCommandResponse.result;
    const error = result?.error;

    if (error && error.code !== 200) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: error.message,
      });
    }

    setIsStartLoading(false);
    setIsStopLoading(false);
  }, [dataMap]);

  useEffect(() => {
    if (preConfirmSocketId && dataMap[preConfirmSocketId]) {
      const response = getData(preConfirmSocketId);
      if (response) {
        setPreConfirmResult(response);
        setIsPreConfirmLoading(false);
      }
    }
  }, [dataMap, preConfirmSocketId]);

  useEffect(() => {
    if (preConfirmResult) {
      const data = preConfirmResult.data || [];
      const hasSubscribedService = data.some(item => item.service && item.service.length > 0);

      setActivePreConfirmData(data);
      setPreConfirmResult(null);

      if (hasSubscribedService) {
        setShowConfirmServiceChangeModal(true);
      } else {
        if (currentModal === "moveToSite") {
          handleButtonOnClick("moveToSite");
          handleDrawerClose();
        } else {
          setDisplayModal(true);
        }
      }
    }
  }, [preConfirmResult]);

  const sendPreConfirmRequest = (actionType) => {
    setIsPreConfirmLoading(true);
    setPreConfirmResult(null);
    const newSocketId = uuidv4();
    setPreConfirmSocketId(newSocketId);

    const standalone = Object.keys(auth.orgs).length === 0;
    const apiKey = standalone
      ? "/orgs/license/service/subscribe/preConfirm"
      : "/ents/orgs/license/service/subscribe/preConfirm";

    const placeholder = standalone
      ? { orgId: auth.realm.id }
      : { entId: auth.realm.id, orgId: auth.orgs.id };

    const params = [];

    // 1. Source Site (remove)
    const sourceSiteId = isInventoryDevice ? null : (infoData.siteId || auth.sites?.id);
    if (sourceSiteId && sourceSiteId !== "Inventory") {
      params.push({
        siteId: sourceSiteId,
        action: "remove",
        devices: [
          {
            modelName: infoData.modelName,
            deviceId: infoData.macAddress,
          }
        ]
      });
    }

    // 2. Target Site (add)
    const targetSiteId = actionType === "moveToSite" ? selectedMoveSite?.siteId : null;
    if (targetSiteId) {
      params.push({
        siteId: targetSiteId,
        action: "add",
        devices: [
          {
            modelName: infoData.modelName,
            deviceId: infoData.macAddress,
          }
        ]
      });
    }

    sendRequest(apiKey, newSocketId, placeholder, params);
  };

  const permissionsConfig = {
    tunnel_access: capabilityData.tunnelAccess === true,
    org_online:
      auth.scopeLevel === "org" &&
      isInventoryDevice === false &&
      infoData.linkStatus === "online",
    org_inventory_online:
      auth.scopeLevel === "org" &&
      isInventoryDevice === true &&
      infoData.linkStatus === "online",
    org_offline:
      auth.scopeLevel === "org" &&
      isInventoryDevice === false &&
      infoData.linkStatus === "offline",
    org_inventory_offline:
      auth.scopeLevel === "org" &&
      isInventoryDevice === true &&
      infoData.linkStatus === "offline",
    org_onboarding:
      auth.scopeLevel === "org" && infoData.linkStatus === "onboarding",
    site_online:
      auth.scopeLevel === "site" &&
      isInventoryDevice === false &&
      infoData.linkStatus === "online",
    site_inventory_online:
      auth.scopeLevel === "site" &&
      isInventoryDevice === true &&
      infoData.linkStatus === "online",
    site_offline:
      auth.scopeLevel === "site" &&
      isInventoryDevice === false &&
      infoData.linkStatus === "offline",
    site_inventory_offline:
      auth.scopeLevel === "site" &&
      isInventoryDevice === true &&
      infoData.linkStatus === "offline",
    site_onboarding:
      auth.scopeLevel === "site" && infoData.linkStatus === "onboarding",
  };

  const sections = [
    ...(infoData.deviceType !== "proxy"
      ? [
        {
          title: "Remote Access",
          description:
            "Open a direct remote web or terminal session via the secure management tunnel.",
          disabled: false,
          buttons: [
            ...(!isSurveillanceDevice
              ? [
                {
                  label: "Terminal",
                  disabled: false,
                  loading: isTunnelAccesstTerminalLoading,
                  onClick: () => handleButtonOnClick("tunnelAccessTerminal"),
                },
              ]
              : []),
            {
              label: "Web",
              disabled: false,
              loading: isTunnelAccesstWebLoading,
              onClick: () => handleButtonOnClick("tunnelAccessTerminalWeb"),
            },
          ],
          showPermissions: ["tunnel_access"],
        },
      ]
      : []),
    ...(infoData.deviceType !== "proxy" &&
      !isSurveillanceDevice &&
      capabilityData.locateDevice /* && infoData.managedProtocol === "nc" */ //locateDevice = true, managedProtocol = nc的時候才顯示(這條件先拿掉 之後補回去)
      ? [
        {
          title: "Locate Device",
          description: (
            <>
              Let device lights up all LEDs in green. If device doesn't
              receive the stop action,
              <br />
              the LED will back to normal state after 5 minutes.
            </>
          ),
          disabled: false,
          buttons:
            infoData.deviceType === "switch"
              ? [
                {
                  label: "Start",
                  disabled: isStartLoading || isStopLoading,
                  loading: isStartLoading,
                  onClick: () => handleExecuteCommand("lightUpLed"),
                },
                {
                  label: "Stop",
                  disabled: isStartLoading || isStopLoading,
                  loading: isStopLoading,
                  onClick: () => handleExecuteCommand("stoplightUpLed"),
                },
              ]
              : infoData.deviceType === "ap"
                ? [
                  {
                    label: "Start",
                    disabled: isStartLoading || isStopLoading,
                    loading: isStartLoading,
                    onClick: () => handleExecuteCommand("blinkLed"),
                  },
                  {
                    label: "Stop",
                    disabled: isStartLoading || isStopLoading,
                    loading: isStopLoading,
                    onClick: () => handleExecuteCommand("stopBlinkLed"),
                  },
                ]
                : [],

          showPermissions: [
            "org_online",
            "org_inventory_online",
            "site_online",
          ],
        },
      ]
      : []),
    // {
    //     title: "Custom Upgrade",
    //     description: "Choose the firmware file and upgrade the device.",
    //     handleOnClick: null,
    //     disabled: false,
    //     button: "Browse",
    //     showPermissions: ['org_online', 'org_inventory_online', 'site_online']
    // }
    {
      title: "Reboot Device",
      description: "The Device will go offline during the reboot process.",
      handleOnClick: () => {
        setCurrentModal("rebootDevice");
        setDisplayModal(true);
      },
      disabled: false,
      button: "Reboot",
      showPermissions: [
        "org_online",
        "org_inventory_online",
        "site_online",
        "site_inventory_online",
      ],
    },
    ...(infoData.deviceType !== "proxy"
      ? [
        {
          title: "Move to Site",
          description: "Move this device to another site.",
          withSelect: true,
          handleOnClick: () => {
            setCurrentModal("moveToSite");
            if (licenseManagementEnabled) {
              sendPreConfirmRequest("moveToSite");
            } else {
              handleButtonOnClick("moveToSite");
              handleDrawerClose();
            }
          },
          disabled: selectedMoveSite === null,
          button: "Move",
          showPermissions: [
            "org_online",
            "org_inventory_online",
            "org_offline",
            "org_inventory_offline",
            "org_onboarding",
            "site_inventory_online",
            "site_inventory_offline",
          ],
        },
      ]
      : []),
    ...(!isSurveillanceDevice
      ? [{
        title: "Remove from Site",
        description:
          "Remove device from current site but remain in the organization.",
        handleOnClick: () => {
          setCurrentModal("removeFromSite");
          if (licenseManagementEnabled) {
            sendPreConfirmRequest("removeFromSite");
          } else {
            setDisplayModal(true);
          }
        },
        disabled: false,
        button: "Remove",
        showPermissions: [
          "org_online",
          "org_offline",
          "site_online",
          "site_offline",
        ],
      }]
      : []),
    {
      title: "Delete Device",
      description:
        isSurveillanceDevice ? (
          "Remove device from current site but remain in the organization."
        ) : infoData.deviceType === "proxy" ? (
          <>
            The device will be deleted from the organization and all of its
            child devices
            <br />
            will be removed.
          </>
        ) : (
          "The device will be deleted from the organization."
        ),
      handleOnClick: () => {
        setCurrentModal("deleteDevice");
        if (licenseManagementEnabled) {
          sendPreConfirmRequest("deleteDevice");
        } else {
          setDisplayModal(true);
        }
      },
      disabled: false,
      button: "Delete",
      showPermissions: [
        "org_online",
        "org_inventory_online",
        "org_offline",
        "org_inventory_offline",
        "org_onboarding",
        ...(isSurveillanceDevice
          ? [
            "site_online",
            "site_offline",
            "site_inventory_online",
            "site_inventory_offline",
          ]
          : []),
        "site_onboarding",
      ],
    },
  ];

  const modalContent = {
    rebootDevice: {
      title: "Reboot Device",
      description: "Are you sure you want to reboot this device? The device will go offline during the reboot process.",
      handleOnClick: () => {
        handleExecuteCommand("reboot");
        setDisplayModal(false);
      },
      button: "Reboot",
    },
    removeFromSite: {
      title: "Remove Device",
      description: "Are you sure you want to remove this device from site?",
      handleOnClick: () => {
        handleButtonOnClick("removeFromSite");
        setDisplayModal(false);
        handleDrawerClose()
      },
      button: "Remove",
    },
    rebootDevice: {
      title: "Reboot Device",
      description: "This process will take a few minutes, and your device will be temporarily disconnected.",
      handleOnClick: () => {
        handleExecuteCommand("reboot");
        setDisplayModal(false);
      },
      button: "Reboot Now",
      buttonWidth: "130px",
    },
    deleteDevice: {
      title: "Delete Device",
      description: isSurveillanceDevice
        ? "Are you sure you want to delete this device? After the device is deleted, all its related alerts will also be deleted, but the videos stored locally will NOT be deleted."
        : "Are you sure you want to delete this device?",
      handleOnClick: () => {
        handleButtonOnClick("deleteDevice");
        setDisplayModal(false);
        handleDrawerClose()
      },
      button: "Delete",
    },
  };

  useEffect(() => {
    if (
      !(
        permissionsConfig.site_online ||
        permissionsConfig.site_offline ||
        permissionsConfig.site_onboarding
      )
    ) {
      if (Object.keys(auth.orgs).length === 0) {
        sendRequest("/orgs/:orgId/sites.read", sitesUuid, {
          orgId: auth.realm.id,
        });
      } else {
        sendRequest("/ents/:entId/orgs/:orgId/sites.read", sitesUuid, {
          entId: auth.realm.id,
          orgId: auth.orgs.id,
        });
      }
    }
  }, [infoData.macAddress]);

  useEffect(() => {
    let qOrgId = auth.realm.id;
    let qSiteId = auth.sites?.id ?? infoData.siteId;

    const sitesResponse = getData(sitesUuid);
    const tunnelAccessTerminalResponse = getData(tunnelAccessTerminalUuid);
    const tunnelAccessWebResponse = getData(tunnelAccessWebUuid);

    if (sitesResponse) {
      setSitesData(sitesResponse.data.sites);
    }

    if (tunnelAccessTerminalResponse) {
      if (tunnelAccessTerminalResponse.status === "success") {
        let finalEndpoint = tunnelAccessTerminalResponse.endpoint;
        if (!isPremises) {
          let append = encodeURIComponent(`|${qOrgId}|${qSiteId}`);
          finalEndpoint = `${finalEndpoint}${append}`;
        }
        window.open(finalEndpoint, "_blank");
      }
      if (tunnelAccessTerminalResponse.code) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: tunnelAccessTerminalResponse.message,
        });
      }
      setIsTunnelAccesstTerminalLoading(false);
    }
    if (tunnelAccessWebResponse) {
      if (tunnelAccessWebResponse.status === "success") {
        let finalEndpoint = tunnelAccessWebResponse.endpoint;
        if (!isPremises) {
          let append = encodeURIComponent(`|${qOrgId}|${qSiteId}`);
          finalEndpoint = `${finalEndpoint}${append}`;
        }
        window.open(finalEndpoint, "_blank");
      }
      if (tunnelAccessWebResponse.code) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: tunnelAccessWebResponse.message,
        });
      }
      setIsTunnelAccesstWebLoading(false);
    }
  }, [dataMap]);

  useEffect(() => {
    return () => {
      setSelectedMoveSite(null);
    };
  }, [infoData.macAddress]);

  return (
    <>
      <CollapseCard title="Management">
        {sections
          .filter(({ showPermissions }) =>
            showPermissions.some((perm) => permissionsConfig[perm])
          )
          .map(
            (
              {
                title,
                description,
                withSelect,
                handleOnClick,
                disabled,
                button,
                buttons,
              },
              index,
              array
            ) => (
              <React.Fragment key={title}>
                <Box sx={{ margin: "15px" }}>
                  <StyledTypography>{title}</StyledTypography>
                  <p style={{ fontSize: theme.typography.customFontSize[2] }}>
                    {description}
                  </p>

                  {withSelect && (
                    <Box margin="15px 0">
                      <MenuSelect
                        placeholder="Please select..."
                        value={selectedMoveSite?.value || ""}
                        onChange={(opt) => setSelectedMoveSite(opt)}
                        options={sitesData.map((site) => ({
                          label: site.siteName,
                          value: site.siteId,
                          ...site,
                        }))}
                        pinningOptions={[
                          {
                            value: "Inventory",
                            label: "Inventory",
                            siteId: null,
                          },
                        ]}
                        disabled={sitesData.length === 0}
                      />
                    </Box>
                  )}
                  {/* 支援多個按鈕 */}
                  {buttons ? (
                    <Box sx={{ display: "flex", gap: "10px" }}>
                      {buttons.map((btn, i) => (
                        <OutlineButton
                          key={i}
                          width="100px"
                          height="32px"
                          disabled={btn.disabled || btn.loading}
                          onClick={btn.onClick}
                        >
                          {btn.loading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              {btn.label}
                              <CircularProgress
                                size={16}
                                sx={{ color: theme.palette.primary.greyThird }}
                              />
                            </Box>
                          ) : (
                            btn.label
                          )}
                        </OutlineButton>
                      ))}
                    </Box>
                  ) : (
                    <OutlineButton
                      width="100px"
                      height="32px"
                      disabled={disabled}
                      onClick={handleOnClick}
                    >
                      {button}
                    </OutlineButton>
                  )}
                </Box>

                {index !== array.length - 1 && <Divider />}
              </React.Fragment>
            )
          )}
      </CollapseCard>
      {displayModal && (
        <ModalContainer display={displayModal} modalWidth={"500px"}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
              }}
            >
              {modalContent[currentModal].title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "25px 0",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
              }}
            >
              {modalContent[currentModal].description}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "10px 0",
            }}
          >
            <Box margin="0 10px">
              <OutlineButton
                width="100px"
                height="32px"
                onClick={() => setDisplayModal(false)}
              >
                Cancel
              </OutlineButton>
            </Box>
            <Box margin="0 10px">
              <SolidWarnButton
                width={modalContent[currentModal].buttonWidth || "100px"}
                height="32px"
                onClick={modalContent[currentModal].handleOnClick}
              >
                {modalContent[currentModal].button}
              </SolidWarnButton>
            </Box>
          </Box>
        </ModalContainer>
      )}

      {isPreConfirmLoading && (
        <ModalContainer display={isPreConfirmLoading} modalWidth="600px">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        </ModalContainer>
      )}

      {showConfirmServiceChangeModal && (
        <ModalContainer display={showConfirmServiceChangeModal} modalWidth="600px">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <Typography sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}>
              Confirm Service Change
            </Typography>
            <IconButton
              onClick={() => setShowConfirmServiceChangeModal(false)}
              sx={{ p: 0.5 }}
            >
              <CloseOutlined style={{ fontSize: "20px" }} />
            </IconButton>
          </Box>
          <Box sx={{ position: "relative" }}>
            <Stack spacing={2.5}>
              <Box sx={{ overflowY: "auto", maxHeight: "75vh" }}>
                <Stack spacing={3}>
                  {(() => {
                    if (
                      (currentModal === 'removeFromSite' || currentModal === 'deleteDevice' || currentModal === 'moveToSite') &&
                      activePreConfirmData
                    ) {
                      const serviceNameMapping = {
                        healthAI: "Health AI Insight",
                        nmsPro: "NMS Pro"
                      };
                      const sitesToRender = activePreConfirmData.filter(item => item.service && item.service.length > 0);
                      return (
                        <>
                          {sitesToRender.map((siteItem, idx) => {
                            const serviceText = siteItem.service.map(s => serviceNameMapping[s] || s).join(", ") || "Subscribed Services";
                            const actionText = siteItem.action === "add" ? "Add Device" : siteItem.action === "remove" ? "Remove Device" : siteItem.action;
                            const dailyRateValue = siteItem.dailyRate || 0;
                            const totalCredits = dailyRateValue * (siteItem.remainingDays || 0);

                            return (
                              <Stack key={idx} spacing={2} sx={{ mb: idx < sitesToRender.length - 1 ? 3 : 0 }}>
                                <Stack spacing={1}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <CaretRightFilled style={{ fontSize: "14px", color: "#172664" }} />
                                    <Typography sx={{ color: "rgba(0,0,0,0.85)", fontSize: "15px", fontWeight: "bold" }}>
                                      {siteItem.siteName || "Site"}
                                    </Typography>
                                  </Stack>
                                  <Typography sx={{ color: "#172664", fontSize: "16px", fontWeight: "bold", mb: 0.5 }}>
                                    Service: {serviceText}
                                  </Typography>
                                  <Divider sx={{ mb: 1.5 }} />

                                  <Stack spacing={0.5}>
                                    <Stack direction="row" spacing={1}>
                                      <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                        Action:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: siteItem.action === "remove" ? "#D1003B" : "#008514", fontWeight: "bold" }}>
                                        {actionText}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                      <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                        Devices:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: siteItem.action === "remove" ? "#D1003B" : "#008514", fontWeight: "bold" }}>
                                        {siteItem.deviceCount}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                      <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                        Rate:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "#373737", fontWeight: "bold" }}>
                                        {siteItem.rate || ""}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Stack>

                                <Box sx={{ p: 2, border: "1px solid rgba(0,0,0,0.15)", borderRadius: "12px", mt: 2 }}>
                                  <Typography sx={{ color: "#172664", fontSize: "14px", fontWeight: "bold", mb: 2 }}>
                                    {siteItem.action === "remove" ? "This Month’s Prorated Credit Refund" : "This Month’s Prorated Credit Deduction"}
                                  </Typography>
                                  <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="body2" color="textSecondary">
                                        Daily Rate
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                        <Box component="span" sx={{ color: "#008514" }}>
                                          {dailyRateValue.toLocaleString()}
                                        </Box> Credits / day
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="body2" color="textSecondary">
                                        Remaining Days
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                        <Box component="span" sx={{ color: "#008514" }}>
                                          {siteItem.remainingDays} Days
                                        </Box>
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="body2" color="textSecondary">
                                        Total
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                        <Box component="span" sx={{ color: "#008514" }}>
                                          {totalCredits.toLocaleString()}
                                        </Box> Credits
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Box>
                              </Stack>
                            );
                          })}
                        </>
                      );
                    }
                    return null;
                  })()}

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, alignItems: "flex-start" }}>
                    <WarningFilled style={{ color: "#D1003B", fontSize: "18px" }} />
                    <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.85)", fontWeight: "bold" }}>
                      {getIsInsufficient()
                        ? "Insufficient credits. Please contact your Organization Super Admin."
                        : "Changes take effect immediately upon confirmation. Credits will be auto-deducted monthly based on your subscription plan."}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
                <OutlineButton
                  onClick={() => setShowConfirmServiceChangeModal(false)}
                  width="120px"
                  height="36px"
                  sx={{ borderRadius: "20px" }}
                >
                  Cancel
                </OutlineButton>
                <SolidButton
                  onClick={() => {
                    setShowConfirmServiceChangeModal(false);
                    if (currentModal === "moveToSite") {
                      handleButtonOnClick("moveToSite");
                    } else if (currentModal === "deleteDevice") {
                      handleButtonOnClick("deleteDevice");
                    } else {
                      handleButtonOnClick("removeFromSite");
                    }
                    handleDrawerClose();
                  }}
                  disabled={getIsInsufficient()}
                  width="120px"
                  height="36px"
                  sx={{ borderRadius: "20px", bgcolor: "#172664" }}
                >
                  Confirm
                </SolidButton>
              </Stack>
            </Stack>
          </Box>
        </ModalContainer>
      )}

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default ManagementPanel;
